/**
 * What may be uploaded where: the same lists and limits as the API (backend apps/medialib/validators.py,
 * bulkupload/services/safety.py, vehicles/services/csv_io.py, products/services/stock.py). The browser checks first
 * so people get an instant, clear message; the server always checks again (and also looks inside the file).
 */

export interface FileRule {
  /** Lower-case extensions without the dot. */
  extensions: readonly string[];
  /** MIME types browsers report for them (used with the extensions for the file picker's filter). */
  mimeTypes: readonly string[];
  /** Largest size of one file, in bytes. */
  maxBytes: number;
  /** A lower limit for some extensions (pictures are capped lower than PDFs). */
  maxBytesByExtension?: Readonly<Record<string, number>>;
}

const MB = 1024 * 1024;

export const FILE_RULES = {
  /** Photos and pictures: no SVG (it can carry scripts), no HEIC/BMP. */
  image: {
    extensions: ["jpg", "jpeg", "png", "webp", "gif"],
    mimeTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
    maxBytes: 10 * MB,
  },
  document: { extensions: ["pdf"], mimeTypes: ["application/pdf"], maxBytes: 20 * MB },
  /** Ticket attachments, KYC documents. */
  imageOrDocument: {
    extensions: ["jpg", "jpeg", "png", "webp", "gif", "pdf"],
    mimeTypes: ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"],
    maxBytes: 20 * MB,
    maxBytesByExtension: { jpg: 10 * MB, jpeg: 10 * MB, png: 10 * MB, webp: 10 * MB, gif: 10 * MB },
  },
  /** Vehicle master import. */
  vehiclesCsv: { extensions: ["csv"], mimeTypes: ["text/csv"], maxBytes: 5 * MB },
  /** Stock update import. */
  stockCsv: { extensions: ["csv"], mimeTypes: ["text/csv"], maxBytes: 1 * MB },
  /** Bulk product upload: the sheet alone, or a ZIP with the sheet and its images. */
  bulkSheet: {
    extensions: ["xlsx", "xlsm"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
    ],
    maxBytes: 20 * MB,
  },
  bulkZip: { extensions: ["zip"], mimeTypes: ["application/zip"], maxBytes: 100 * MB },
} as const satisfies Record<string, FileRule>;

/** The `accept` attribute for a file input: ".jpg,.jpeg,…,image/jpeg,…". */
export function acceptOf(rule: FileRule): string {
  return [...rule.extensions.map((ext) => `.${ext}`), ...rule.mimeTypes].join(",");
}

/** "JPG, PNG, WEBP or GIF" style list for hints and messages. */
export function typesOf(rule: FileRule): string {
  return [
    ...new Set(rule.extensions.map((ext) => (ext === "jpeg" ? "jpg" : ext).toUpperCase())),
  ].join(", ");
}

export function formatBytes(bytes: number): string {
  if (bytes >= MB) return `${Math.round((bytes / MB) * 10) / 10} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

/** Whether a file matches an `accept` string (".pdf", "image/png", "image/*"); no accept = anything. */
export function matchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept?.trim()) return true;
  const ext = `.${extensionOf(file.name)}`;
  const type = file.type.toLowerCase();
  return accept
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .some((token) =>
      token.startsWith(".")
        ? token === ext
        : token.endsWith("/*")
          ? type.startsWith(token.slice(0, -1))
          : token === type,
    );
}

export interface FileProblem {
  file: File;
  reason: "type" | "size";
  /** The size limit that applied (for "size"). */
  limit?: number;
}

/** The size limit for one file under a rule. */
export function limitFor(file: File, rule: FileRule): number {
  return rule.maxBytesByExtension?.[extensionOf(file.name)] ?? rule.maxBytes;
}

/** Splits picked or dropped files into the ones to upload and the ones refused (wrong type, too large). */
export function checkFiles(
  files: File[],
  { accept, maxBytes, rule }: { accept?: string; maxBytes?: number; rule?: FileRule },
): { accepted: File[]; refused: FileProblem[] } {
  const accepted: File[] = [];
  const refused: FileProblem[] = [];
  for (const file of files) {
    const limit = rule && maxBytes === rule.maxBytes ? limitFor(file, rule) : maxBytes;
    if (!matchesAccept(file, accept)) refused.push({ file, reason: "type" });
    else if (limit !== undefined && file.size > limit)
      refused.push({ file, reason: "size", limit });
    else accepted.push(file);
  }
  return { accepted, refused };
}
